import { useCallback, useEffect, useState } from "react";
import {
	Text,
	Spinner,
	Alert,
	AlertIcon,
} from "@chakra-ui/react";
import { FiPlus, FiTrash2, FiSave, FiMove } from "react-icons/fi";

import { Button } from "newui/components/Button";
import { useUnitsAndMeasures } from "newui/store";
import { cn } from "utils/helpers";
import { showToast } from "utils/helpers";
import type {
	ScheduleColumn,
	ScheduleColumnType,
	ScheduleRow,
	ScheduleOfRatesData,
} from "./schedule-of-rates.types";
import {
	getCompanyScheduleOfRates,
	saveCompanyScheduleOfRates,
} from "../../api";

export interface CompanyScheduleSettingsProps {
	companyId: number;
}

const COLUMN_TYPE_LABELS: Record<ScheduleColumnType, string> = {
	text: "Text",
	unit_of_measurement: "Unit",
	number: "Number",
	currency: "Currency",
};

const COLUMN_TYPE_OPTIONS: { label: string; value: ScheduleColumnType }[] = [
	{ value: "text", label: "Text Column" },
	{ value: "unit_of_measurement", label: "Unit of Measurement Column" },
	{ value: "number", label: "Number Column" },
	{ value: "currency", label: "Currency Column" },
];

const cellBorder = "border border-[#EAECF0]";

const generateLocalId = (): number => {
	return -Math.floor(Math.random() * 1_000_000_000);
};

const CompanyScheduleSettings: React.FC<CompanyScheduleSettingsProps> = ({
	companyId,
}) => {
	const [columns, setColumns] = useState<ScheduleColumn[]>([]);
	const [rows, setRows] = useState<ScheduleRow[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [saveSuccess, setSaveSuccess] = useState(false);
	const [dragOverColId, setDragOverColId] = useState<number | null>(null);
	const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
	const { values: unitsAndMeasures } = useUnitsAndMeasures();

	const fetchSchedule = useCallback(async () => {
		setIsLoading(true);
		try {
			const data = await getCompanyScheduleOfRates(companyId);
			if (data) {
				setColumns(data.columns);
				setRows(data.rows);
			}
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsLoading(false);
		}
	}, [companyId]);

	useEffect(() => {
		fetchSchedule();
	}, [fetchSchedule]);

	const handleAddColumn = useCallback(
		(columnType: ScheduleColumnType) => {
			const nextSortOrder = columns.length > 0
				? Math.max(...columns.map((c) => c.sortOrder)) + 1
				: 1;
			const newCol: ScheduleColumn = {
				id: nextSortOrder,
				label: `Column ${columns.length + 1}`,
				columnType,
				sortOrder: nextSortOrder,
			};
			setColumns([...columns, newCol]);
		},
		[columns]
	);

	const handleRemoveColumn = useCallback(
		(colId: number) => {
			setColumns(columns.filter((c) => c.id !== colId));
			setRows(
				rows.map((row) => {
					const newValues = { ...row.values };
					delete newValues[colId];
					return { ...row, values: newValues };
				})
			);
		},
		[columns, rows]
	);

	const handleColumnReorder = useCallback(
		(sourceColId: number, targetColId: number) => {
			if (sourceColId === targetColId) {
				return;
			}
			const sorted = [...columns].sort((a, b) => a.sortOrder - b.sortOrder);
			const sourceIdx = sorted.findIndex((c) => c.id === sourceColId);
			const targetIdx = sorted.findIndex((c) => c.id === targetColId);
			if (sourceIdx === -1 || targetIdx === -1) {
				return;
			}
			const [moved] = sorted.splice(sourceIdx, 1);
			sorted.splice(targetIdx, 0, moved);

			const idMap: Record<number, number> = {};
			const reordered = sorted.map((col, idx) => {
				const newSortOrder = idx + 1;
				idMap[col.id] = newSortOrder;
				return { ...col, sortOrder: newSortOrder, id: newSortOrder };
			});

			const reKeyedRows = rows.map((row) => {
				const newValues: Record<number, string> = {};
				for (const [oldKey, value] of Object.entries(row.values)) {
					const newKey = idMap[Number(oldKey)];
					if (newKey !== undefined) {
						newValues[newKey] = value;
					}
				}
				return { ...row, values: newValues };
			});

			setColumns(reordered);
			setRows(reKeyedRows);
		},
		[columns, rows]
	);

	const handleDragStart = useCallback(
		(e: React.DragEvent<HTMLTableCellElement>, colId: number) => {
			e.dataTransfer.setData("text/plain", String(colId));
			e.dataTransfer.effectAllowed = "move";
		},
		[]
	);

	const handleDragOver = useCallback(
		(e: React.DragEvent<HTMLTableCellElement>, colId: number) => {
			e.preventDefault();
			e.dataTransfer.dropEffect = "move";
			setDragOverColId(colId);
		},
		[]
	);

	const handleDragLeave = useCallback(() => {
		setDragOverColId(null);
	}, []);

	const handleDrop = useCallback(
		(e: React.DragEvent<HTMLTableCellElement>, targetColId: number) => {
			e.preventDefault();
			setDragOverColId(null);
			const sourceColId = Number(e.dataTransfer.getData("text/plain"));
			handleColumnReorder(sourceColId, targetColId);
		},
		[handleColumnReorder]
	);

	const handleColumnLabelChange = useCallback(
		(colId: number, label: string) => {
			setColumns(
				columns.map((c) => (c.id === colId ? { ...c, label } : c))
			);
		},
		[columns]
	);

	const handleColumnTypeChange = useCallback(
		(colId: number, columnType: ScheduleColumnType) => {
			setColumns(
				columns.map((c) => (c.id === colId ? { ...c, columnType } : c))
			);
		},
		[columns]
	);

	const handleAddRow = useCallback(() => {
		const newRow: ScheduleRow = {
			id: generateLocalId(),
			sortOrder: rows.length + 1,
			values: {},
		};
		setRows([...rows, newRow]);
	}, [rows]);

	const handleRemoveRow = useCallback(
		(rowId: number) => {
			setRows(rows.filter((r) => r.id !== rowId));
		},
		[rows]
	);

	const handleCellChange = useCallback(
		(rowId: number, colId: number, value: string) => {
			setRows(
				rows.map((row) => {
					if (row.id === rowId) {
						return {
							...row,
							values: { ...row.values, [colId]: value },
						};
					}
					return row;
				})
			);
			if (value.trim()) {
				setValidationErrors((prev) => {
					const next = new Set(prev);
					next.delete(`${rowId}-${colId}`);
					return next;
				});
			}
		},
		[rows]
	);

	const handleSave = useCallback(async () => {
		const errors = new Set<string>();
		rows.forEach((row) => {
			columns.forEach((col) => {
				const value = row.values[col.id] ?? "";
				if (!value.trim()) {
					errors.add(`${row.id}-${col.id}`);
				}
			});
		});
		if (errors.size > 0) {
			setValidationErrors(errors);
			showToast("Please fill in all fields before saving", "Error", false);
			return;
		}
		setValidationErrors(new Set());
		setIsSaving(true);
		setSaveSuccess(false);
		try {
			await saveCompanyScheduleOfRates(companyId, {
				name: "Company Schedule of Rates",
				columns: columns.map((c) => ({
					name: c.label,
					type: c.columnType,
					sort_order: c.sortOrder,
				})),
				rows: rows.map((r) => ({
					sort_order: r.sortOrder,
					cells: r.values,
				})),
			});
			setSaveSuccess(true);
			showToast("Schedule of rates saved successfully", "Success", true);
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsSaving(false);
		}
	}, [companyId, columns, rows]);

	if (isLoading) {
		return (
			<div
				className="flex items-center justify-center py-12"
				data-testid="company-schedule-loading"
			>
				<Spinner size="lg" />
			</div>
		);
	}

	return (
		<div data-testid="company-schedule-settings">
			<div className="mb-4 flex items-center justify-between">
				<div>
					<Text className="font-roboto text-lg font-semibold text-gray-900">
						Schedule of Rates
					</Text>
					<Text className="text-sm text-gray-500">
						Define your company&apos;s default schedule of rates template. This
						will be used as the starting point when adding a schedule to new
						submissions.
					</Text>
				</div>
				{columns.length > 0 && (
					<Button
						size="sm"
						btntype="primary"
						onClick={handleSave}
						disabled={isSaving}
						data-testid="save-company-schedule-btn"
					>
						<FiSave className="mr-1" />
						{isSaving ? "Saving..." : "Save Template"}
					</Button>
				)}
			</div>

			{saveSuccess && (
				<Alert
					status="success"
					className="mb-4 rounded-md"
					data-testid="save-success-alert"
				>
					<AlertIcon />
					<Text className="text-sm">
						Schedule of rates template saved successfully.
					</Text>
				</Alert>
			)}

			<div className="mb-3 flex items-center gap-2">
				<div className="flex gap-1">
					{COLUMN_TYPE_OPTIONS.map((opt) => (
						<Button
							key={opt.value}
							size="xs"
							btntype="secondary"
							onClick={() => handleAddColumn(opt.value)}
							data-testid={`company-add-col-${opt.value}`}
						>
							<FiPlus className="mr-1" />
							{opt.label}
						</Button>
					))}
				</div>
			</div>

			{columns.length > 0 && (
				<div className="overflow-x-auto rounded-xl border border-[#EAECF0] bg-white shadow-sm">
					<table
						className="w-full table-auto"
						data-testid="company-schedule-table"
					>
						<caption className="sr-only">Company schedule of rates template</caption>
						<thead>
							<tr className="bg-[#F9FAFB]">
								<th
									className={cn(
										cellBorder,
										"w-10 px-2 py-2 text-center font-roboto text-xs font-medium text-tertiary-600"
									)}
								>
									#
								</th>
								{columns.map((col) => (
									<th
										key={col.id}
										className={cn(
											cellBorder,
											"relative cursor-move px-2 py-2 font-roboto text-xs font-medium text-tertiary-600",
											dragOverColId === col.id && "bg-blue-100"
										)}
										style={{ minWidth: "140px" }}
										draggable
										onDragStart={(e) => handleDragStart(e, col.id)}
										onDragOver={(e) => handleDragOver(e, col.id)}
										onDragLeave={handleDragLeave}
										onDrop={(e) => handleDrop(e, col.id)}
									>
										<FiMove
											size={10}
											className="absolute left-1 top-1 text-gray-400"
											title="Drag to reorder"
										/>
										<div className="mb-1">
											<input
												type="text"
												className="w-full rounded border border-transparent bg-transparent px-1 text-xs font-medium hover:border-gray-300 focus:border-blue-400 focus:outline-none"
												value={col.label}
												onChange={(e) =>
													handleColumnLabelChange(col.id, e.target.value)
												}
												onMouseDown={(e) => e.stopPropagation()}
												aria-label={`Column label for ${col.label}`}
												data-testid={`company-col-label-${col.id}`}
											/>
										</div>
										<div className="flex items-center gap-1">
											<select
												className="flex-1 rounded border border-gray-200 bg-white px-1 py-0.5 text-[10px]"
												value={col.columnType}
												onChange={(e) =>
													handleColumnTypeChange(
														col.id,
														e.target.value as ScheduleColumnType
													)
												}
												onMouseDown={(e) => e.stopPropagation()}
												aria-label={`Column type for ${col.label}`}
												data-testid={`company-col-type-${col.id}`}
											>
												{COLUMN_TYPE_OPTIONS.map((opt) => (
													<option key={opt.value} value={opt.value}>
														{opt.label}
													</option>
												))}
											</select>
											<button
												type="button"
												className="rounded p-0.5 text-red-400 hover:text-red-600"
												onClick={() => handleRemoveColumn(col.id)}
												title="Remove column"
												aria-label={`Remove column ${col.label}`}
												data-testid={`company-remove-col-${col.id}`}
											>
												<FiTrash2 size={10} />
											</button>
										</div>
									</th>
								))}
								<th className="w-10 bg-[#F9FAFB]" />
							</tr>
						</thead>
						<tbody>
							{rows.map((row, idx) => (
								<tr key={row.id} className="border-b border-[#EAECF0]">
									<td
										className={cn(
											cellBorder,
											"text-center font-roboto text-xs text-primary-500 bg-gray-50"
										)}
									>
										{idx + 1}
									</td>
									{columns.map((col) => {
										const value = row.values[col.id] ?? "";
										const hasError = validationErrors.has(`${row.id}-${col.id}`);
										const errorBorder = hasError ? "border-red-500" : "border-gray-300";
										if (col.columnType === "unit_of_measurement") {
											return (
												<td key={col.id} className={cn(cellBorder, "px-1 py-1")}>
													<select
														className={cn("w-full rounded border bg-white px-2 py-1 text-sm", errorBorder)}
														value={value}
														onChange={(e) =>
															handleCellChange(row.id, col.id, e.target.value)
														}
														data-testid={`company-cell-${row.id}-${col.id}`}
													>
														<option value="">Select unit</option>
														{unitsAndMeasures?.map((unit) => (
															<option key={unit.value} value={unit.value}>
																{unit.label}
															</option>
														))}
													</select>
												</td>
											);
										}
										return (
											<td key={col.id} className={cn(cellBorder, "px-1 py-1")}>
												<input
													type={col.columnType === "number" || col.columnType === "currency" ? "number" : "text"}
													className={cn("w-full rounded border bg-white px-2 py-1 text-sm", errorBorder)}
													value={value}
													onChange={(e) =>
														handleCellChange(row.id, col.id, e.target.value)
													}
													placeholder={col.columnType === "currency" ? "0.00" : ""}
													step={col.columnType === "currency" ? "0.01" : undefined}
													min={col.columnType === "number" || col.columnType === "currency" ? 0 : undefined}
													data-testid={`company-cell-${row.id}-${col.id}`}
												/>
											</td>
										);
									})}
									<td className="px-1">
										<button
											type="button"
											className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600"
											onClick={() => handleRemoveRow(row.id)}
											title="Remove row"
											aria-label={`Remove row ${idx + 1}`}
											data-testid={`company-remove-row-${row.id}`}
										>
											<FiTrash2 size={12} />
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{columns.length > 0 && (
				<div className="mt-3">
					<Button
						size="xs"
						btntype="secondary"
						onClick={handleAddRow}
						data-testid="company-add-row-btn"
					>
						<FiPlus className="mr-1" />
						Add Row
					</Button>
				</div>
			)}
		</div>
	);
};

export { CompanyScheduleSettings };
